import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SelectFormInput, TextFormInput } from "src/components/form";
import { LaboratoryFinanceBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ILaboratoryFinanceReduxFormState } from "src/reducers/reactReduxForms/laboratoryFinance";
import * as LaboratoryFinanceActions from "../../../actions/laboratoryFinance";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { LaboratorySubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./LabFinancePage.scss";

interface LaboratoryFinancePageProps {
    form: Dtos.LaboratoryFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ILaboratoryFinanceReduxFormState;
    laboratoryFinance: Dtos.LaboratoryFinance;
    lookups: Dtos.Lookup[];
    loadingLaboratoryFinance: boolean;
    loadLaboratoryFinanceSuccess: boolean;
    loadLaboratoryFinanceFailure: boolean;
    loadLaboratoryFinanceErrorCode: string;
    savingLaboratoryFinance: boolean;
    saveLaboratoryFinanceSuccess: boolean;
    saveLaboratoryFinanceFailure: boolean;
    validatingLaboratoryFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearLaboratoryFinance: LaboratoryFinanceActions.ILaboratoryFinanceClearActionCreator;
    loadLaboratoryFinance: LaboratoryFinanceActions.ILaboratoryFinanceLoadByLaboratoryIdActionCreator;
    saveLaboratoryFinance: LaboratoryFinanceActions.ILaboratoryFinanceSaveActionCreator;
    validateLaboratoryFinance: LaboratoryFinanceActions.ILaboratoryFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.laboratoryFinance";

const LaboratoryFinancePage: FC<LaboratoryFinancePageProps> = ({
    form,
    formState,
    formProperties,
    laboratoryFinance,
    lookups,
    loadingLaboratoryFinance,
    loadLaboratoryFinanceFailure,
    loadLaboratoryFinanceErrorCode,
    savingLaboratoryFinance,
    saveLaboratoryFinanceSuccess,
    saveLaboratoryFinanceFailure,
    validatingLaboratoryFinance,
    validationFailures,
    permissions,
    clearLaboratoryFinance,
    loadLaboratoryFinance,
    saveLaboratoryFinance,
    validateLaboratoryFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Laboratory Finance
    useEffect(() => {
        if (laboratoryIdNumber && !laboratoryFinance) {
            setBreadcrumbs(LaboratoryFinanceBreadcrumbs(laboratoryIdNumber));
            setDrawerItems(LaboratorySubMenu(laboratoryIdNumber, permissions));

            loadLaboratoryFinance(laboratoryIdNumber);
        } else if (laboratoryFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        laboratoryIdNumber,
        laboratoryFinance,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratoryFinance,
        LaboratoryFinanceBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryFinanceFailure) {
            navigate(`/error/${loadLaboratoryFinanceErrorCode}`);
        }
    }, [
        loadLaboratoryFinanceFailure,
        loadLaboratoryFinanceErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (laboratoryIdNumber && laboratoryFinance?.laboratory) {
            setBreadcrumbs(
                LaboratoryFinanceBreadcrumbs(
                    laboratoryIdNumber,
                    laboratoryFinance.laboratory?.name
                )
            );
        }
    }, [
        laboratoryIdNumber,
        laboratoryFinance,
        setBreadcrumbs,
        LaboratoryFinanceBreadcrumbs,
    ]);

    // Reload laboratory finance after saving
    useEffect(() => {
        if (!savingLaboratoryFinance && saveLaboratoryFinanceSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Laboratory finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/labs`;
                navigate(url);
            } else if (!laboratoryIdNumber) {
                const url = `/labs/${laboratoryIdNumber}/finance`;
                navigate(url);
                clearLaboratoryFinance();
            }
        }
    }, [
        savingLaboratoryFinance,
        saveLaboratoryFinanceSuccess,
        laboratoryIdNumber,
        navigate,
        saveAndReturn,
        clearLaboratoryFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingLaboratoryFinance && saveLaboratoryFinanceFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingLaboratoryFinance, saveLaboratoryFinanceSuccess]);

    // Cleanup effect to clear the laboratory finance on component unmount
    useEffect(() => {
        return () => {
            clearLaboratoryFinance();
            resetForm(reduxFormName);
        };
    }, [clearLaboratoryFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.LaboratoryFinance = {} as Dtos.LaboratoryFinance;

            if (!data.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            await saveLaboratoryFinance(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.LaboratoryFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = "/labs";
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.LaboratoryFinance = {} as Dtos.LaboratoryFinance;

        loadForm(
            reduxFormName,
            Object.assign({ ...laboratoryFinance }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.LaboratoryFinance = {} as Dtos.LaboratoryFinance;

            if (form && hasBeenSubmitted && !validatingLaboratoryFinance) {
                await validateLaboratoryFinance(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                laboratoryIdNumber,
                null,
                permissions,
                Dtos.Permission.LabFinanceUpdate,
                Dtos.Permission.LabFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingLaboratoryFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryFinance",
                                        {
                                            bctInvoicingPrefix: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "LaboratoryFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "laboratoryFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "laboratoryFinance",
                                        {
                                            fundingEmailAddress: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "laboratoryFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingLaboratoryFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        laboratoryFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingLaboratoryFinance || savingLaboratoryFinance}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<LaboratoryFinancePageProps> => {
    let laboratoryFinance: Dtos.LaboratoryFinance | undefined = !(
        state.laboratoryFinance.formData instanceof Array
    )
        ? state.laboratoryFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratoryFinance: laboratoryFinance!,
        loadingLaboratoryFinance:
            state.laboratoryFinance.loadState &&
            state.laboratoryFinance.loadState.status == RequestState.Pending,
        loadLaboratoryFinanceSuccess:
            state.laboratoryFinance.loadState &&
            state.laboratoryFinance.loadState.status == RequestState.Success,
        loadLaboratoryFinanceFailure:
            state.laboratoryFinance.loadState &&
            state.laboratoryFinance.loadState.status == RequestState.Failure,
        loadLaboratoryFinanceErrorCode:
            state.laboratoryFinance.loadState.errorCode,
        savingLaboratoryFinance:
            state.laboratoryFinance.saveState &&
            state.laboratoryFinance.saveState.status == RequestState.Pending,
        saveLaboratoryFinanceSuccess:
            state.laboratoryFinance.saveState &&
            state.laboratoryFinance.saveState.status == RequestState.Success,
        saveLaboratoryFinanceFailure:
            state.laboratoryFinance.saveState &&
            state.laboratoryFinance.saveState.status == RequestState.Failure,
        validatingLaboratoryFinance:
            state.laboratoryFinance.validateState &&
            state.laboratoryFinance.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.laboratoryFinance.lookups,
        form: state.reduxForms.laboratoryFinance,
        formState: state.laboratoryFinance.formState,
        formProperties: state.laboratoryFinance.formProperties,
        reduxFormState: state.reduxForms.formState.laboratoryFinance,
        validationFailures: laboratoryFinance
            ? state.laboratoryFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<LaboratoryFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadLaboratoryFinance: bindActionCreators(
            LaboratoryFinanceActions.LoadLaboratoryFinanceByLaboratoryId,
            dispatch
        ),
        clearLaboratoryFinance: bindActionCreators(
            LaboratoryFinanceActions.Clear,
            dispatch
        ),
        saveLaboratoryFinance: bindActionCreators(
            LaboratoryFinanceActions.SaveLaboratoryFinance,
            dispatch
        ),
        validateLaboratoryFinance: bindActionCreators(
            LaboratoryFinanceActions.ValidateLaboratoryFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LaboratoryFinancePage);
